import Text from '~/common/components/Texts/Text';
import { PROPERTY_REVIEW_LEVEL_BY_NUMBER } from '~/common/constants';
import type { LoanSummary } from '~/common/hooks/useGetLoanSummary';
import { useVAIRRRLCheck } from '~/common/hooks/useVAIRRRLCheck';
import Grid from '~/common/mui/Grids/GridItem';
import { formatCurrency } from '~/common/util/format';
import lang from '~/modals/LoanSummaryDialog/language.json';

import {
  LabelRowGroup,
  LabelText,
  SpacedRow,
  StyledDivider,
  TopLabel,
} from './Styled';

type LoanSummaryPropertyTabProps = Readonly<{
  loanInfo: LoanSummary['loanInfo'];
  propertyData: LoanSummary['propertyData'];
  miscellaneousInfo: LoanSummary['miscellaneousInfo'];
}>;

export function LoanSummaryPropertyTab({
  loanInfo,
  propertyData,
  miscellaneousInfo,
}: LoanSummaryPropertyTabProps) {
  const isFHA = loanInfo.loanProductType === 'FHA';
  const isVAIRRRL = useVAIRRRLCheck();

  return (
    <Grid container spacing={2} sx={{ display: 'flex', flexDirection: 'row' }}>
      <Grid
        id="property-details"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          minWidth: '340px',
          mt: '1rem',
        }}
      >
        <TopLabel>{lang.PROPERTY_DETAILS}</TopLabel>
        <LabelRowGroup>
          <SpacedRow>
            <LabelText>{lang.OCCUPANCY}</LabelText>
            <Text sx={{ marginTop: '0 !important' }}>
              {propertyData.occupancyType ?? lang.PLACEHOLDER}
            </Text>
          </SpacedRow>
          <StyledDivider />
          <SpacedRow sx={{ wordWrap: 'normal' }}>
            <LabelText>{lang.PROPERTY_TYPE}</LabelText>
            <Text sx={{ marginTop: '0 !important' }}>
              {propertyData.propertyType ?? lang.PLACEHOLDER}
            </Text>
          </SpacedRow>
          <StyledDivider />
          <SpacedRow>
            <LabelText>{lang.ATTACHED_DETACHED}</LabelText>
            <Text>
              {propertyData.isAttachedProperty ? 'Attached' : 'Detached'}
            </Text>
          </SpacedRow>
        </LabelRowGroup>
        <LabelRowGroup>
          {!isVAIRRRL && (
            <>
              <SpacedRow>
                <LabelText>{lang.ASSIGNED_AMC}</LabelText>
                <Text>{miscellaneousInfo?.amcName ?? lang.PLACEHOLDER}</Text>
              </SpacedRow>
              <StyledDivider />
              {!isFHA && (
                <>
                  <SpacedRow>
                    <LabelText>{lang.PROPERTY_REVIEW_LEVEL}</LabelText>
                    <Text>
                      {PROPERTY_REVIEW_LEVEL_BY_NUMBER[
                        miscellaneousInfo?.propertyReviewLevel
                      ] ?? lang.PLACEHOLDER}
                    </Text>
                  </SpacedRow>
                  <StyledDivider />
                </>
              )}
            </>
          )}
          <SpacedRow>
            <LabelText>{lang.APPRAISED_VALUE}</LabelText>
            <Text>
              {formatCurrency(loanInfo.appraisedValue) ?? lang.PLACEHOLDER}
            </Text>
          </SpacedRow>
        </LabelRowGroup>
      </Grid>
    </Grid>
  );
}
